import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const EventSuccessModal = ({ event, onClose }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const eventUrl = `${window.location.origin}/e/${event.slug}`;

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(eventUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = (platform) => {
    const text = `Check out this event: ${event.title}`;
    const encodedUrl = encodeURIComponent(eventUrl);
    const encodedText = encodeURIComponent(text);

    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      email: `mailto:?subject=${encodeURIComponent(event.title)}&body=${encodedText}%0A%0A${encodedUrl}`,
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], "_blank", "width=600,height=400");
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden"
        >
          {/* Success Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-6 text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold mb-2">Event Published Successfully!</h2>
            <p className="text-green-100">Your event page is now live</p>
          </div>

          {/* Content */}
          <div className="p-8 space-y-6">
            {/* Event URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event Page URL
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={eventUrl}
                  readOnly
                  className="flex-1 px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 font-mono text-sm"
                />
                <button
                  onClick={handleCopyUrl}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap"
                >
                  {copied ? "✓ Copied!" : "Copy URL"}
                </button>
              </div>
            </div>

            {/* Share Buttons */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Share Your Event
              </label>
              <div className="grid grid-cols-4 gap-3">
                <button
                  onClick={() => handleShare("twitter")}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all group"
                >
                  <div className="text-2xl">🐦</div>
                  <span className="text-xs font-medium text-gray-600 group-hover:text-blue-600">
                    Twitter
                  </span>
                </button>
                <button
                  onClick={() => handleShare("facebook")}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-600 hover:bg-blue-50 transition-all group"
                >
                  <div className="text-2xl">📘</div>
                  <span className="text-xs font-medium text-gray-600 group-hover:text-blue-600">
                    Facebook
                  </span>
                </button>
                <button
                  onClick={() => handleShare("linkedin")}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-700 hover:bg-blue-50 transition-all group"
                >
                  <div className="text-2xl">💼</div>
                  <span className="text-xs font-medium text-gray-600 group-hover:text-blue-700">
                    LinkedIn
                  </span>
                </button>
                <button
                  onClick={() => handleShare("email")}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-all group"
                >
                  <div className="text-2xl">✉️</div>
                  <span className="text-xs font-medium text-gray-600 group-hover:text-gray-700">
                    Email
                  </span>
                </button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={() => window.open(eventUrl, "_blank")}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left flex items-center justify-between group"
                >
                  <span className="font-medium text-gray-700 group-hover:text-gray-900">
                    👁️ View Event Page
                  </span>
                  <span className="text-gray-400">→</span>
                </button>
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/events/${event.id}/edit`);
                  }}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left flex items-center justify-between group"
                >
                  <span className="font-medium text-gray-700 group-hover:text-gray-900">
                    ✏️ Edit Event
                  </span>
                  <span className="text-gray-400">→</span>
                </button>
                <button
                  onClick={() => navigate("/events")}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left flex items-center justify-between group"
                >
                  <span className="font-medium text-gray-700 group-hover:text-gray-900">
                    📊 View Analytics
                  </span>
                  <span className="text-gray-400">→</span>
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => navigate("/events")}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Back to Events
              </button>
              <button
                onClick={() => navigate("/events/new")}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Create Another Event
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default EventSuccessModal;
